// Customer-benchmark table logic for v3: rows keyed on use case ids (`uc-*`), plus the pure helpers
// the use case pages and the benchmarks editor share. Carried over from v2's engine/benchmarks.ts
// (derived improvements, CSV parse / import / export) with the metric bucketing retargeted from
// leading-indicator levers to use cases. Every side effect (storage, downloads, clock) stays out so
// these run headless.

import { BM_KW } from '../data/benchmarks';
import { USE_CASES } from '../data/use-case-catalog';
import { BENCHMARK_LEVER_TO_USE_CASE, type BmRow } from './migrate-v3';

export interface BmNum {
  n: number;
  pct: boolean;
}

/** First number in a cell. Zero and unparseable cells are rejected. */
export function bmParseNum(t: string | number | null | undefined): BmNum | null {
  const m = String(t || '')
    .replace(/[–—]/g, '-')
    .match(/-?\$?\d[\d,]*\.?\d*/);
  if (!m) return null;
  const n = parseFloat(m[0].replace(/[$,]/g, ''));
  return isFinite(n) && n !== 0 ? { n, pct: /%/.test(String(t)) } : null;
}

/** Derives an improvement string from pre/post when the author left `imp` blank. */
export function bmAuto(r: Pick<BmRow, 'pre' | 'post'>): string {
  const a = bmParseNum(r.pre),
    b = bmParseNum(r.post);
  if (!a || !b) return '';
  if (a.pct && b.pct) {
    const d = Math.round((b.n - a.n) * 10) / 10,
      rel = Math.round(((b.n - a.n) / a.n) * 100);
    return (d >= 0 ? '+' : '−') + Math.abs(d) + ' pts (' + (rel >= 0 ? '+' : '−') + Math.abs(rel) + '%)';
  }
  const ratio = b.n / a.n;
  if (ratio >= 1.95) return Math.round(ratio * 10) / 10 + '× (+' + Math.round((ratio - 1) * 100) + '%)';
  return (ratio >= 1 ? '+' : '−') + Math.abs(Math.round((ratio - 1) * 100)) + '%';
}

/** The effective improvement cell: author text, else the derived one. */
export function bmEff(r: Pick<BmRow, 'imp' | 'pre' | 'post'>): string {
  return (r.imp || '').trim() || bmAuto(r);
}

/** The select options for a row's metric: every catalog use case, then Other. */
export function bmMetricOptions(): { v: string; label: string }[] {
  return [...USE_CASES.map((u) => ({ v: u.id, label: u.name })), { v: 'other', label: 'Other' }];
}

/** A row's metric as text: the use case name, else the free text. */
export function bmMetricLabel(r: Pick<BmRow, 'metricId' | 'metricText'>): string {
  if (r.metricId === 'other') return r.metricText || '';
  const uc = USE_CASES.find((u) => u.id === r.metricId);
  return uc ? uc.name : r.metricText || '';
}

/**
 * Auto-classifies a free-text metric to a use case id. Exact (case-insensitive) use case name first,
 * then v2's ordered keyword rules (first match wins) carried through the lever → use case map.
 * Keywords of retired levers resolve to `other`: the text is kept, nothing is dropped.
 */
export function bmBucket(text: string | null | undefined): string {
  const t = (text || '').trim().toLowerCase();
  if (!t) return 'other';
  const uc = USE_CASES.find((u) => u.name.toLowerCase() === t);
  if (uc) return uc.id;
  for (const [re, lever] of BM_KW) if (re.test(t)) return BENCHMARK_LEVER_TO_USE_CASE[lever] ?? 'other';
  return 'other';
}

/** Comma/tab delimited with RFC-4180 double-quote escaping. Blank rows dropped. */
export function bmParseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [],
    cur = '',
    q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cur += '"';
          i++;
        } else q = false;
      } else cur += c;
    } else if (c === '"') q = true;
    else if (c === ',' || c === '\t') {
      row.push(cur);
      cur = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(cur);
      cur = '';
      if (row.some((x) => x.trim() !== '')) rows.push(row);
      row = [];
    } else cur += c;
  }
  row.push(cur);
  if (row.some((x) => x.trim() !== '')) rows.push(row);
  return rows;
}

export interface BmImportResult {
  /** Rows to append to the current table. Empty when `ok` is false. */
  rows: BmRow[];
  note: string;
  ok: boolean;
}

/**
 * Header-aware CSV/TSV import. `idBase` stands in for a timestamp so ids stay deterministic; row ids
 * are `'b' + idBase + '_' + index`.
 */
export function bmImportText(text: string, idBase: string): BmImportResult {
  const rows = bmParseCSV(text || '');
  if (!rows.length) return { rows: [], note: 'Nothing to import — paste CSV or TSV rows first.', ok: false };
  let cols = [0, 1, 2, 3, 4, 5],
    start = 0;
  const h = rows[0].map((x) => x.toLowerCase());
  if (h.some((x) => /customer|company|metric|indicator|improve|note/.test(x))) {
    start = 1;
    const find = (re: RegExp, def: number) => {
      const i = h.findIndex((x) => re.test(x));
      return i < 0 ? def : i;
    };
    cols = [
      find(/customer|company|account|name/, 0),
      find(/metric|indicator|use case/, 1),
      find(/pre/, 2),
      find(/post/, 3),
      find(/improve|delta|lift|change/, 4),
      find(/note|desc|context|comment/, 5),
    ];
  }
  const added = rows
    .slice(start)
    .map((r, i): BmRow => {
      const g = (j: number) => (r[cols[j]] || '').trim();
      const mt = g(1);
      return {
        id: 'b' + idBase + '_' + i,
        customer: g(0),
        metricId: bmBucket(mt),
        metricText: mt,
        pre: g(2),
        post: g(3),
        imp: g(4),
        notes: g(5),
      };
    })
    .filter((r) => r.customer || r.metricText || r.notes || r.post);
  if (!added.length) return { rows: [], note: 'No usable rows found.', ok: false };
  const bucketed = added.filter((r) => r.metricId !== 'other').length;
  return {
    rows: added,
    note:
      'Imported ' +
      added.length +
      ' row' +
      (added.length === 1 ? '' : 's') +
      ' — ' +
      bucketed +
      ' matched to a use case, ' +
      (added.length - bucketed) +
      ' kept as free text.',
    ok: true,
  };
}

/** The CSV text for a download. */
export function bmExportCSV(rows: BmRow[]): string {
  const esc = (v: string | number | null | undefined) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const lines = [
    ['Customer', 'Use case', 'Pre-Clay performance', 'Post-Clay performance', 'Clay improvement', 'Notes'].join(','),
  ];
  rows.forEach((r) => lines.push([r.customer, bmMetricLabel(r), r.pre, r.post, bmEff(r), r.notes].map(esc).join(',')));
  return lines.join('\n');
}

/** Rows matching a search string over customer, metric and notes. */
export function bmSearch(rows: BmRow[], query: string): BmRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => `${r.customer} ${bmMetricLabel(r)} ${r.metricText} ${r.notes}`.toLowerCase().includes(q));
}

export type BmSortCol = 'customer' | 'metric' | 'pre' | 'post' | 'imp' | 'notes';

/** Stable sort by one column; `dir` 1 ascending, -1 descending. No column = the order given. */
export function bmSort(rows: BmRow[], col: BmSortCol | '', dir: 1 | -1): BmRow[] {
  if (!col) return rows;
  const key = (r: BmRow): string =>
    col === 'metric' ? bmMetricLabel(r) : col === 'imp' ? bmEff(r) : r[col];
  return [...rows].sort((a, b) => key(a).localeCompare(key(b), undefined, { numeric: true, sensitivity: 'base' }) * dir);
}
